"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, use, useCallback, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useIsHomeRoute } from "@/hooks/use-is-home-route";

/**
 * Coordinates the collapsible mobile header search between the toggle button (in
 * the header actions) and the search input (in the middle grid column). The refs
 * are part of the contract: `inputRef` so `open()` can focus synchronously
 * inside the tap gesture, `toggleRef` so a dismissal can return focus to it.
 */
type MobileSearchContextValue = {
  isOpen: boolean;
  open: () => void;
  close: (options?: {
    returnFocus?: boolean;
  }) => void;
  toggleRef: React.RefObject<HTMLButtonElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
};
const MobileSearchContext = createContext<MobileSearchContextValue | null>(null);
export function MobileSearchProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);
  const inputRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      flushSync(() => setIsOpen(true));
      inputRef.current?.focus();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const open = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = options => {
      if (options?.returnFocus) {
        flushSync(() => setIsOpen(false));
        toggleRef.current?.focus();
        return;
      }
      setIsOpen(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const close = t2;
  const isHome = useIsHomeRoute();
  const [wasHome, setWasHome] = useState(isHome);
  if (isHome !== wasHome) {
    setWasHome(isHome);
    if (isHome) {
      setIsOpen(false);
    }
  }
  let t3;
  if ($[2] !== isOpen) {
    t3 = {
      isOpen,
      open,
      close,
      toggleRef,
      inputRef
    };
    $[2] = isOpen;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const value = t3;
  let t4;
  if ($[4] !== children || $[5] !== value) {
    t4 = <MobileSearchContext value={value}>{children}</MobileSearchContext>;
    $[4] = children;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function useMobileSearch(): MobileSearchContextValue {
  const context = use(MobileSearchContext);
  if (!context) {
    throw new Error("useMobileSearch must be used within a MobileSearchProvider");
  }
  return context;
}